import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ArrowRight, Loader2, Mail, X, Sparkles } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { getRfiEmailTemplate, sendRfiEmail } from '@/lib/api/rfi';
import { BackendError } from '@/lib/backend';

interface RFIEmailEditorProps {
  projectId: string;
  onContinue: () => void;
  onSkip: () => void;
}

export const RFIEmailEditor = ({ projectId, onContinue, onSkip }: RFIEmailEditorProps) => {
  const [recipients, setRecipients] = useState<string[]>([]);
  const [recipientInput, setRecipientInput] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [hasGenerated, setHasGenerated] = useState(false);

  const handleGenerateDraft = useCallback(async () => {
    setIsGenerating(true);
    try {
      const template = await getRfiEmailTemplate(projectId);
      setSubject(template.subject);
      setBody(template.body);
      setHasGenerated(true);
      toast({ title: 'Email draft generated' });
    } catch (e) {
      const message =
        e instanceof BackendError
          ? `${e.message} (HTTP ${e.status})`
          : e instanceof Error
            ? e.message
            : 'Unknown error';
      toast({
        title: 'Failed to generate email draft',
        description: message,
        variant: 'destructive',
      });
    } finally {
      setIsGenerating(false);
    }
  }, [projectId]);

  const handleAddRecipient = useCallback(() => {
    const email = recipientInput.trim().toLowerCase();
    if (!email) return;
    
    // Simple email validation
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast({
        title: 'Invalid email address',
        variant: 'destructive',
      });
      return;
    }
    
    if (recipients.includes(email)) {
      toast({
        title: 'Email already added',
        variant: 'destructive',
      });
      return;
    }
    
    setRecipients(prev => [...prev, email]);
    setRecipientInput('');
  }, [recipientInput, recipients]);

  const handleRemoveRecipient = useCallback((email: string) => {
    setRecipients(prev => prev.filter(r => r !== email));
  }, []);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddRecipient();
    }
  }, [handleAddRecipient]);

  const handleSend = useCallback(async () => {
    if (recipients.length === 0) {
      toast({
        title: 'No recipients',
        description: 'Please add at least one recipient.',
        variant: 'destructive',
      });
      return;
    }
    
    if (!subject.trim()) {
      toast({
        title: 'Subject required',
        description: 'Please enter an email subject.',
        variant: 'destructive',
      });
      return;
    }
    
    if (!body.trim()) {
      toast({
        title: 'Email body required',
        description: 'Please enter the email content.',
        variant: 'destructive',
      });
      return;
    }
    
    setIsSending(true);
    try {
      await sendRfiEmail(projectId, {
        subject: subject.trim(),
        body: body.trim(),
        recipients,
      });
      toast({ title: 'RFI email sent successfully' });
      onContinue();
    } catch (e) {
      const message =
        e instanceof BackendError
          ? `${e.message} (HTTP ${e.status})`
          : e instanceof Error
            ? e.message
            : 'Unknown error';
      toast({
        title: 'Failed to send email',
        description: message,
        variant: 'destructive',
      });
    } finally {
      setIsSending(false);
    }
  }, [projectId, recipients, subject, body, onContinue]);

  return (
    <div className="space-y-4 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold text-foreground tracking-tight">
          Send RFI Email
        </h1>
        <div className="flex items-center gap-3">
          <Button variant="ghost" onClick={onSkip} size="sm">
            Skip
          </Button>
          <Button 
            onClick={handleSend} 
            size="sm" 
            className="gap-2"
            disabled={isSending || recipients.length === 0 || !subject.trim() || !body.trim()}
          >
            {isSending ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Sending...
              </>
            ) : (
              <>
                Send & Continue
                <ArrowRight className="w-4 h-4" />
              </>
            )}
          </Button>
        </div>
      </div>

      <p className="text-sm text-muted-foreground">
        Add recipients and customize the email draft
      </p>

      <Card className="border-border">
        <CardContent className="p-6 space-y-6">
          {/* Recipients */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">Recipients</label>
            <div className="flex gap-2">
              <Input
                placeholder="Enter email address"
                value={recipientInput}
                onChange={(e) => setRecipientInput(e.target.value)}
                onKeyDown={handleKeyDown}
                className="flex-1"
              />
              <Button 
                variant="outline" 
                onClick={handleAddRecipient}
                disabled={!recipientInput.trim()}
              >
                Add
              </Button>
            </div>
            {recipients.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {recipients.map((email) => (
                  <Badge key={email} variant="secondary" className="gap-1.5 py-1 px-2">
                    <Mail className="w-3 h-3" />
                    {email}
                    <button
                      onClick={() => handleRemoveRecipient(email)}
                      className="ml-1 hover:text-destructive transition-colors"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            )}
          </div>

          {/* Subject */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">Subject</label>
            <Input
              placeholder="Email subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
            />
          </div>

          {/* Email Body */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-foreground">Email Draft</label>
              <Button
                variant="outline"
                size="sm"
                onClick={handleGenerateDraft}
                disabled={isGenerating}
                className="gap-2"
              >
                {isGenerating ? (
                  <>
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-3.5 h-3.5" />
                    {hasGenerated ? 'Regenerate Draft' : 'Generate Draft'}
                  </>
                )}
              </Button>
            </div>
            {!hasGenerated && !body ? (
              <div className="border border-dashed border-border rounded-md p-8 text-center">
                <Mail className="w-8 h-8 mx-auto text-muted-foreground/50 mb-3" />
                <p className="text-sm text-muted-foreground">
                  Click "Generate Draft" to create an email based on the RFI items
                </p>
              </div>
            ) : (
              <Textarea
                placeholder="Email content..."
                value={body}
                onChange={(e) => setBody(e.target.value)}
                className="min-h-[300px] resize-none"
              />
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
